import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone, Linkedin, Download } from "lucide-react";
import { fadeUp, viewportOnce } from "./anim";
import { profile } from "@/data/resume";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — I'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email me directly.");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full border-b border-black/15 bg-transparent py-4 font-body text-lg font-light text-[#1A1A18] outline-none transition-colors placeholder:text-[#A3A29A] focus:border-[#9A7526]";

  return (
    <footer
      id="contact"
      data-testid="contact"
      className="relative mx-auto max-w-[1500px] px-6 pb-16 pt-24 md:px-12 md:pt-40"
    >
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        <span className="font-mono-x text-xs uppercase tracking-[0.3em] text-[#9A7526]">
          (08) — Contact
        </span>
        <h2 className="mt-8 font-display text-6xl font-light uppercase leading-[0.9] tracking-tight text-[#1A1A18] sm:text-7xl md:text-8xl lg:text-[10vw]">
          Let's talk<span className="text-[#9A7526]">.</span>
        </h2>
      </motion.div>

      <div className="mt-20 grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
        {/* Details */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="space-y-8 lg:col-span-5"
        >
          <p className="max-w-md font-body text-lg font-light leading-relaxed text-[#6E6D66]">
            Open to leadership conversations in Customer Success, enterprise
            account management and AI-driven CS transformation.
          </p>

          <div className="space-y-5">
            <a
              href={`mailto:${profile.email}`}
              data-testid="contact-email"
              className="group flex items-center gap-4 font-body text-[#1A1A18] transition-colors hover:text-[#9A7526]"
            >
              <Mail size={18} className="text-[#9A7526]" />
              {profile.email}
              <ArrowUpRight size={14} className="opacity-0 transition-opacity group-hover:opacity-100" />
            </a>
            <a
              href={profile.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin"
              className="group flex items-center gap-4 font-body text-[#1A1A18] transition-colors hover:text-[#9A7526]"
            >
              <Linkedin size={18} className="text-[#9A7526]" />
              LinkedIn — /mihir-nadkarni
              <ArrowUpRight size={14} className="opacity-0 transition-opacity group-hover:opacity-100" />
            </a>
            <a
              href={`tel:${profile.phone.replace(/\s/g, "")}`}
              data-testid="contact-phone"
              className="flex items-center gap-4 font-body text-[#1A1A18] transition-colors hover:text-[#9A7526]"
            >
              <Phone size={18} className="text-[#9A7526]" />
              {profile.phone}
            </a>
            <div className="flex items-center gap-4 font-body text-[#6E6D66]">
              <MapPin size={18} className="text-[#9A7526]" />
              {profile.location}
            </div>
          </div>

          <a
            href={`${process.env.PUBLIC_URL || ""}/Mihir_Nadkarni_CV.pdf`}
            download
            data-testid="contact-download-cv"
            className="group inline-flex items-center gap-3 rounded-full border border-black/20 px-6 py-3 font-mono-x text-xs uppercase tracking-[0.2em] text-[#1A1A18] transition-colors hover:border-[#9A7526] hover:text-[#9A7526]"
          >
            <Download size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            Download résumé (PDF)
          </a>
        </motion.div>

        {/* Form */}
        <motion.form
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          onSubmit={submit}
          data-testid="contact-form"
          className="space-y-8 lg:col-span-7"
        >
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <input
              className={field}
              placeholder="Your name"
              data-testid="contact-input-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              className={field}
              placeholder="Your email"
              type="email"
              data-testid="contact-input-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <textarea
            className={`${field} resize-none`}
            placeholder="Tell me about the opportunity"
            rows={4}
            data-testid="contact-input-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="group inline-flex items-center gap-3 rounded-full bg-[#1A1A18] px-8 py-4 font-mono-x text-xs uppercase tracking-[0.2em] text-[#FBFAF7] transition-colors hover:bg-[#9A7526] disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send message"}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </motion.form>
      </div>

      <div className="mt-24 flex flex-col items-start justify-between gap-4 border-t border-black/10 pt-8 md:flex-row md:items-center">
        <span className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-[#A3A29A]">
          © {new Date().getFullYear()} {profile.name}
        </span>
        <span className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-[#A3A29A]">
          Customer Success · Mumbai, India
        </span>
      </div>
    </footer>
  );
};
